/**
 * AI Family Safety Assistant (§22.1) — daily summary at the top of home.
 *
 * Plain-language, coach-tone digest of the family's week, with up to three
 * recommendations and a single one-tap action. Backed by the Claude API when
 * configured, otherwise a deterministic rule-based summary (same shape).
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { analyticsApi } from '@/api/analytics';

export function AiSummaryCard() {
  const { t } = useTranslation();
  const summary = useQuery({
    queryKey: ['ai-summary'],
    queryFn: () => analyticsApi.aiSummary(),
    staleTime: 10 * 60_000,
    retry: false,
  });

  const s = summary.data;

  if (summary.isPending) {
    return (
      <Card className="border-[color:var(--kz-color-brand-primary)]/30 bg-[color:var(--kz-color-brand-primary)]/5">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Skeleton className="size-9 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!s) {
    return (
      <Card className="border-[color:var(--kz-color-brand-primary)]/30 bg-[color:var(--kz-color-brand-primary)]/5">
        <CardContent className="p-5">
          <p className="text-sm text-[color:var(--kz-color-ink-secondary)]">
            {t('ai_summary.empty')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[color:var(--kz-color-brand-primary)]/30 bg-[color:var(--kz-color-brand-primary)]/5">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-[color:var(--kz-color-brand-primary)]/15">
            <Sparkles className="size-5 text-[color:var(--kz-color-brand-primary)]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold">{t('ai_summary.title')}</p>
              {s.source === 'ai' && (
                <span className="rounded-full bg-[color:var(--kz-color-brand-primary)]/15 px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--kz-color-brand-primary)]">
                  {t('ai_summary.ai_badge')}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-[color:var(--kz-color-ink-secondary)]">{s.summary}</p>

            {s.recommendations.length > 0 && (
              <ul className="mt-2 space-y-1">
                {s.recommendations.map((r, i) => (
                  <li key={i} className="flex gap-2 text-xs text-[color:var(--kz-color-ink-muted)]">
                    <span className="text-[color:var(--kz-color-brand-primary)]">•</span>
                    {r}
                  </li>
                ))}
              </ul>
            )}

            {s.suggested_action && (
              <Button asChild size="sm" variant="outline" className="mt-3">
                <Link to={s.suggested_action.route}>
                  {s.suggested_action.label}
                  <ArrowRight className="ml-1 size-3.5" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
